/**
 * /sequences/new — minimal create-then-redirect page. The actual
 * step editor lives at /sequences/:id.
 */
import { useState, type FormEvent } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { api } from "../../../lib/api.js";
import { useAuth } from "../../../lib/auth-context.js";

export const Route = createFileRoute("/_app/sequences/new")({
  component: NewSequencePage,
});

function NewSequencePage() {
  const { activeIgAccount } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [defaultMessageTag, setDefaultMessageTag] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function create(e: FormEvent) {
    e.preventDefault();
    if (!name.trim() || !activeIgAccount || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ sequence: { id: string } }>("/sequences", {
        name: name.trim(),
        description: description.trim() || null,
        igAccountId: activeIgAccount.id,
        defaultMessageTag: defaultMessageTag || null,
      });
      void navigate({ to: "/sequences/$sequenceId", params: { sequenceId: r.data.sequence.id } });
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
      setError(msg ?? "Failed to create");
      setBusy(false);
    }
  }

  return (
    <section style={{ maxWidth: 600, margin: "0 auto" }}>
      <h1 style={{ margin: "0 0 24px", fontSize: 28 }}>New sequence</h1>
      <div style={{ fontSize: 13, marginBottom: 12, color: "var(--text-muted)" }}>
        Sending from <strong>@{activeIgAccount?.igUsername}</strong>
      </div>
      <form onSubmit={create} className="brut-card" style={{ padding: 14, display: "grid", gap: 10 }}>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Sequence name (e.g. Welcome series)"
          className="brut-input"
          maxLength={120}
          required
        />
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Description (optional)"
          className="brut-input"
          maxLength={1024}
        />
        <label className="brut-label">Default message tag (optional)</label>
        <select
          value={defaultMessageTag}
          onChange={(e) => setDefaultMessageTag(e.target.value)}
          className="brut-input"
        >
          <option value="">(none — must be inside 24h window)</option>
          <option value="ACCOUNT_UPDATE">ACCOUNT_UPDATE</option>
        </select>
        <div style={{ fontSize: 12, color: "var(--muted)" }}>
          A tag bypasses the 24h messaging window per Meta policy. Use only when
          the message qualifies — most drip steps should leave this empty and
          accept that contacts outside the window will be skipped.
        </div>

        {error ? <div style={{ color: "#b40000", fontSize: 13 }}>{error}</div> : null}
        <button type="submit" disabled={!name.trim() || !activeIgAccount || busy} className="brut-button">
          {busy ? "Creating…" : "Create"}
        </button>
      </form>
    </section>
  );
}
